import { scaleLinear } from "d3";
import { Text } from "@visx/text";
import { getFillColor } from "./utils";
import { useOptionalTooltipDispatchRef } from "../../hooks/TooltipStateIsolationProvider";
import type { TooltipProductDatum } from "../../types/atlasTooltip";
import { rca as toRCA, pci as toPCI, distance as toDistance } from "@atlas-int/data/types";
import type { RCA, PCI, COG, Distance } from "@atlas-int/data/types";
import { text, interactive } from "@atlas-int/tokens";
import type { HierarchyNode } from "d3-hierarchy";
import type { ProductMetadatum } from "@atlas-int/data/types";
import { ColorBy } from "@atlas-int/data";
import type { ProcessedNode } from "./types";

const getTextAnchor = (angle: number) => {
  if (angle >= 0 && angle < Math.PI / 2) {
    return "start";
  } else if (angle >= Math.PI / 2 && angle < Math.PI) {
    return "end";
  } else if (angle >= Math.PI && angle < (3 * Math.PI) / 2) {
    return "end";
  } else {
    return "start";
  }
};

const getVerticalAnchor = (angle: number) => {
  if (angle >= 0 && angle <= Math.PI) {
    return "start";
  } else {
    return "end";
  }
};

const moveTextAwayFromCenter = (x: number, y: number, angle: number, radius: number) => {
  const translateX = x + radius * Math.cos(angle);
  const translateY = y + radius * Math.sin(angle);
  return `translate(${translateX}, ${translateY})`;
};

const truncateText = (text: string, maxLength: number = 15) => {
  return text.length > maxLength ? text.slice(0, maxLength) + "..." : text;
};

type MetaDataNode = HierarchyNode<ProductMetadatum>;

interface ProductRingProps {
  linkLookup: Record<string, string[]>;
  selection: string;
  nodeLookup: Map<string, ProcessedNode>;
  metaDataLookup: Map<string, MetaDataNode>;
  colorMap: Map<string, string>;
  setRingItem: (id: string) => void;
  highlighted: string | null;
  highlight: (id: string | null) => void;
  colorBySelection: ColorBy;
  clusterColorLookup: Map<string, { cluster_col?: string }>;
  complexityColorScale: (value: number | undefined) => string;
  totalExportLookup: Map<string, { pci?: number }>;
  countryExportLookup: Map<string, { exportRca?: number; exportValue?: number; distance?: number; cog?: number }>;
  rcaThreshold: number;
  productClass: string;
}

const ProductRing = ({
  linkLookup,
  selection,
  nodeLookup,
  metaDataLookup,
  colorMap,
  setRingItem,
  highlighted,
  highlight,
  colorBySelection,
  clusterColorLookup,
  complexityColorScale,
  totalExportLookup,
  countryExportLookup,
  rcaThreshold,
  productClass,
}: ProductRingProps) => {
  const width = 600;
  const height = 600;
  const dispatchRef = useOptionalTooltipDispatchRef();

  const focusNode = nodeLookup.get(selection)!;
  const focusProductData = metaDataLookup.get(focusNode.productId)?.data;
  const focusProductExportData = focusProductData
    ? countryExportLookup.get(focusProductData.productId)
    : undefined;
  const focusTopParentId = focusProductData?.topParent?.productId || "";
  const fellowNodes = (linkLookup[selection] || []).map((n: string) => nodeLookup.get(n)).filter((d): d is ProcessedNode => !!d);

  const angleScale = scaleLinear()
    .domain([0, fellowNodes.length])
    .range([0, 2 * Math.PI]);
  const radius = Math.min(width, height) / 2 - 120;

  const centerX = width / 2;
  const centerY = height / 2;
  const { fill: parentFill } = getFillColor(
    focusNode as unknown as Parameters<typeof getFillColor>[0],
    focusProductData as Parameters<typeof getFillColor>[1],
    focusTopParentId,
    colorBySelection as Parameters<typeof getFillColor>[3],
    colorMap,
    clusterColorLookup,
    complexityColorScale,
    totalExportLookup,
    [],
    countryExportLookup,
    rcaThreshold,
  );

  const buildTooltipDatum = (
    productData: ProductMetadatum | undefined,
    exportData: { exportRca?: number; exportValue?: number; distance?: number; cog?: number } | undefined,
    topParentId: string | undefined,
  ): TooltipProductDatum => ({
    kind: "product",
    nameEn: productData?.nameShortEn,
    productCode: productData?.code,
    productId: productData?.productId,
    topLevelParent: topParentId,
    distance: exportData?.distance != null ? toDistance(exportData.distance) : undefined,
    exportValue: exportData?.exportValue,
    exportRca: exportData?.exportRca != null ? toRCA(exportData.exportRca) : undefined,
    pci: totalExportLookup.get(productData?.productId)?.pci != null ? toPCI(totalExportLookup.get(productData?.productId)!.pci!) : undefined,
  });

  return (
    <svg
      style={{ position: "absolute", inset: 0, display: "block" }}
      width="100%"
      height="100%"
      viewBox={`0 0 ${height} ${width}`}
      preserveAspectRatio="xMidYMid meet"
    >
      <circle
        cx={centerX}
        cy={centerY}
        r={radius}
        fill="none"
        stroke={interactive.muted}
      />
      <circle
        cx={centerX}
        cy={centerY}
        r={nodeLookup.get(selection).r}
        fill={parentFill}
        onMouseEnter={() => {
          if (dispatchRef) {
            dispatchRef.current.showTooltip(
              buildTooltipDatum(focusProductData, focusProductExportData, focusTopParentId),
            );
          }
        }}
        onMouseLeave={() => dispatchRef?.current.hideTooltip()}
      />
      <Text
        x={centerX}
        y={centerY + nodeLookup.get(selection).r}
        textAnchor="middle"
        width={100}
        verticalAnchor="start"
      >
        {focusProductData.nameShortEn}
      </Text>
      {fellowNodes.map((d, i) => {
        const productData = metaDataLookup.get(d.productId)?.data;
        const productExportData = countryExportLookup.get(
          productData?.productId,
        );
        const topParentId = productData?.topParent?.productId;
        const x = centerX + radius * Math.cos(angleScale(i) - Math.PI / 2);
        const y = centerY + radius * Math.sin(angleScale(i) - Math.PI / 2);
        const angle = angleScale(i) - Math.PI / 2;
        const r = nodeLookup.get(d.productId).r;
        const transform = moveTextAwayFromCenter(x, y, angle, r + 10);
        const { fill } = getFillColor(
          d as unknown as Parameters<typeof getFillColor>[0],
          productData as Parameters<typeof getFillColor>[1],
          topParentId || "",
          colorBySelection,
          colorMap,
          clusterColorLookup,
          complexityColorScale,
          totalExportLookup,
          [],
          countryExportLookup,
          rcaThreshold,
        );
        return (
          <g key={`ring-node-${d.productId}-${i}`}>
            <circle
              cx={x}
              cy={y}
              r={r}
              fill={fill}
              onClick={() => setRingItem(d.productId)}
              cursor="pointer"
              strokeWidth="2"
              stroke={text.primary}
              strokeOpacity={d.productId === highlighted ? 1 : 0}
              onMouseEnter={() => {
                highlight(d.productId);
                if (dispatchRef) {
                  dispatchRef.current.showTooltip(
                    buildTooltipDatum(productData, productExportData, topParentId),
                  );
                }
              }}
              onMouseLeave={() => {
                highlight(null);
                dispatchRef?.current.hideTooltip();
              }}
            />
            <Text
              dy="0.35em"
              textAnchor={getTextAnchor(angle)}
              verticalAnchor={getVerticalAnchor(angle)}
              width={125}
              style={{ pointerEvents: "auto", cursor: "default" }}
              transform={transform}
            >
              {truncateText(productData.nameShortEn)}
            </Text>
          </g>
        );
      })}
    </svg>
  );
};

export default ProductRing;
